import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { PlayerProfileDTO } from "@jaiho/shared";
import { authWithTelegram, getMe } from "../api/endpoints";
import { setSessionToken } from "../api/client";
import { getRawInitData, initializeTelegramUi } from "../telegram/sdk";

type AuthStatus = "loading" | "ready" | "error";

interface AuthContextValue {
  status: AuthStatus;
  error: string | null;
  me: PlayerProfileDTO | null;
  refreshMe: () => Promise<void>;
  /** Patches the locally-held profile (e.g. after a claim/mutation) without a full refetch. */
  patchMe: (patch: Partial<PlayerProfileDTO>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<PlayerProfileDTO | null>(null);

  const refreshMe = useCallback(async () => {
    const response = await getMe();
    setMe(response);
  }, []);

  useEffect(() => {
    initializeTelegramUi();

    async function login() {
      const initData = getRawInitData();
      if (!initData) {
        setStatus("error");
        setError(
          "Jaiho91 Social must be opened inside Telegram. No Telegram initData was found."
        );
        return;
      }

      try {
        const auth = await authWithTelegram(initData);
        setSessionToken(auth.token);
        await refreshMe();
        setStatus("ready");
      } catch (err) {
        setStatus("error");
        setError(err instanceof Error ? err.message : "Failed to sign in.");
      }
    }

    void login();
  }, [refreshMe]);

  const patchMe = useCallback((patch: Partial<PlayerProfileDTO>) => {
    setMe((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const value = useMemo(
    () => ({ status, error, me, refreshMe, patchMe }),
    [status, error, me, refreshMe, patchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
